import React from 'react';
import { createRoot } from 'react-dom/client'
import App from './App';
import './index.css';
import './i18n';
import * as serviceWorker from './serviceWorker';
import * as Sentry from '@sentry/react';
import localStorageNames from '@data/localStorageNames';
import packageJson from '../package.json';
import { addRxPlugin } from 'rxdb/plugins/core';
import { RxDBDevModePlugin } from 'rxdb/plugins/dev-mode';
import { Amplify } from 'aws-amplify';
import { RxDBQueryBuilderPlugin } from 'rxdb/plugins/query-builder';

addRxPlugin(RxDBDevModePlugin);
addRxPlugin(RxDBQueryBuilderPlugin);

// After a deploy, lazy-loaded chunk filenames change. Any user with the old
// index.html cached will hit a 404 on the new chunk names. Vite fires
// "vite:preloadError" in this case — reload once to pick up the new index.html.
// Once per minute at most: when the chunk is really missing from the server, every load
// fails the same way, and reloading on each one was an endless reload loop (2026-10-08).
const CHUNK_RELOAD_KEY = 'chunk_reload_at';
window.addEventListener('vite:preloadError', (event) => {
  let lastReload = 0;
  try {
    lastReload = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY)) || 0;
  } catch (e) {
    // storage unavailable: treat as never reloaded
  }
  if (Date.now() - lastReload < 60 * 1000) {
    console.error('Chunk failed to load again after a reload; not reloading.', event?.payload);
    return;
  }
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  } catch (e) {
    // storage unavailable: reload anyway, once
  }
  window.location.reload();
});

Amplify.configure({
  API: {
    GraphQL: {
      region: import.meta.env[`VITE_REACT_APP_REGION_${import.meta.env.MODE}`],
      endpoint: import.meta.env[`VITE_REACT_APP_ENDPOINT_AMPLIFY_${import.meta.env.MODE}`],
    }
  }
});


// Create a root
createRoot(document.getElementById('root')).render(
  <App />
)

const outletInfo = JSON.parse(localStorage.getItem(localStorageNames.OUTLET));
const title = import.meta.env[`VITE_REACT_APP_TITLE_${import.meta.env.MODE}`];

Sentry.init({
  release: `RDO_${title}_FE_v` + packageJson.version,
  dsn: import.meta.env[`VITE_REACT_APP_KEY_SENTRY_${import.meta.env.MODE}`],
  environment: import.meta.env[
    `VITE_REACT_APP_SENTRY_ENV_${import.meta.env.MODE}`
  ],
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  initialScope: {
    tags: {
      RDO_CUSTOMER: outletInfo?.outletName,
      RDO_VERSION: packageJson.version,
    },
  },
  ignoreErrors: [
    "Unexpected token '<'",
    'Loading chunk ',
    'Failed to fetch',
    "undefined is not an object (evaluating 'a.N')",
    'Load failed',
    'network error',
    'Attempt to use history.replaceState() more than 100 times per 10 seconds',
    'Unable to preload CSS',
    'Importing a module script failed.',
    'Database deleted by request of the user',
    'AbortError: UnknownError Database deleted by request of the user',
    'BulkError: docs.bulkPut()',
    'RxDatabase with the same name and adapter already exists',
    'RxError (DB8)'
  ],
});
// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
